import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, inr, formatDate, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Bits";
import CameraCaptureButton from "@/components/CameraCaptureButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ComboSelect from "@/components/ComboSelect";
import DuplicateWarningBanner from "@/components/DuplicateWarningBanner";
import { toast, Toaster } from "sonner";
import {
    Receipt, Plus, PaperPlaneTilt, Paperclip, PencilSimple, Trash,
    ArrowsClockwise, Eye, CheckCircle, XCircle, CurrencyInr, Wallet,
} from "@phosphor-icons/react";

const STATUS_CHIPS = {
    draft:                        { label: "Draft",                  bg: "#f5f5f4", fg: "#57534e" },
    pending_approval:             { label: "Awaiting founder",       bg: "#fef3c7", fg: "#92400e" },
    approved:                     { label: "Approved · to pay",      bg: "#dcfce7", fg: "#166534" },
    rejected:                     { label: "Rejected",               bg: "#fee2e2", fg: "#991b1b" },
    pending_bank_disbursement:    { label: "Bank match pending",     bg: "#e0e7ff", fg: "#3730a3" },
    disbursed:                    { label: "Paid",                   bg: "#ccfbf1", fg: "#134e4a" },
};

const TABS_BY_ROLE = {
    founder:    ["mine", "approvals", "disburse", "all"],
    accountant: ["mine", "disburse", "all"],
    // any other role — just "mine"
};

const TAB_LABELS = {
    mine: "My claims",
    approvals: "Approvals",
    disburse: "To reimburse",
    all: "All claims",
};

export default function ExpenseClaims() {
    const { user } = useAuth();
    const tabs = TABS_BY_ROLE[user?.role] || ["mine"];
    const [searchParams] = useSearchParams();
    // Deep-link: ?tab=disburse|approvals|mine|all lands on that tab directly.
    const initialTab = (() => {
        const t = searchParams.get("tab");
        return t && tabs.includes(t) ? t : tabs[0];
    })();
    const [tab, setTab] = useState(initialTab);
    const [claims, setClaims] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);      // claim object being edited (or "new")
    const [viewing, setViewing] = useState(null);      // claim id being viewed
    const [disburseTarget, setDisburseTarget] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [c, lg] = await Promise.all([
                api.get("/expense-claims"),
                api.get("/ledgers"),
            ]);
            setClaims(c.data);
            setLedgers(lg.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    // Deep-link support: /expense-claims?open=<claim_id> opens the detail modal.
    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId) {
            // If the URL points to a pending_approval claim and the current
            // user is an approver, open the review dialog directly.
            const c = claims.find((x) => x.id === openId);
            if (c && c.status === "pending_approval" && ["founder", "accountant"].includes(user?.role)) {
                setEditing(c);
            } else if (c) {
                setViewing(openId);
            } else {
                // Wait for claims to load, fall back to view dialog
                setViewing(openId);
            }
        }
    }, [searchParams, claims, user]);

    const filtered = useMemo(() => {
        if (tab === "mine") return claims.filter((c) => c.claimant_user_id === user?.id);
        if (tab === "approvals") return claims.filter((c) => c.status === "pending_approval");
        if (tab === "disburse") return claims.filter((c) => c.status === "approved" || c.status === "pending_bank_disbursement");
        return claims;
    }, [claims, tab, user]);

    const isPrivileged = ["founder", "accountant"].includes(user?.role);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Expense claims"
                sub="Paid something out of your own pocket for the company? Attach the proof, submit for approval, and get reimbursed."
                right={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="ec-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        <Button onClick={() => setEditing({ new: true, lines: [{}] })}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="ec-new">
                            <Plus size={14} className="mr-2" /> New claim
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-4">
                {/* Tabs */}
                <div className="flex border-b" style={{ borderColor: "var(--se-border)" }}>
                    {tabs.map((t) => {
                        const count = t === "mine"
                            ? claims.filter((c) => c.claimant_user_id === user?.id).length
                            : t === "approvals"
                                ? claims.filter((c) => c.status === "pending_approval").length
                                : t === "disburse"
                                    ? claims.filter((c) => c.status === "approved" || c.status === "pending_bank_disbursement").length
                                    : claims.length;
                        return (
                            <button key={t} onClick={() => setTab(t)}
                                    className={`px-5 py-2.5 text-sm border-b-2 -mb-px ${tab === t ? "border-orange-500 text-orange-600 font-medium" : "border-transparent text-stone-600 hover:text-stone-900"}`}
                                    data-testid={`ec-tab-${t}`}>
                                {TAB_LABELS[t]}
                                {count > 0 ? <span className="ml-2 text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{count}</span> : null}
                            </button>
                        );
                    })}
                </div>

                {/* List */}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    {filtered.length === 0 ? (
                        <div className="p-16 text-center text-stone-500 text-sm">
                            <Receipt size={32} className="mx-auto mb-3 text-stone-300" />
                            {tab === "mine" ? "You haven't filed any claims yet. Click New claim to start." : "Nothing here right now."}
                        </div>
                    ) : (
                        <table className="se-table" data-testid="ec-table">
                            <thead>
                                <tr>
                                    <th>Claim #</th>
                                    <th>Claimant</th>
                                    <th>Date</th>
                                    <th>Reason</th>
                                    <th className="text-right">Amount</th>
                                    <th>Status</th>
                                    <th className="text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c) => {
                                    const chip = STATUS_CHIPS[c.status] || STATUS_CHIPS.draft;
                                    const canEdit = c.claimant_user_id === user?.id && ["draft", "rejected"].includes(c.status);
                                    const canSubmit = c.claimant_user_id === user?.id && ["draft", "rejected"].includes(c.status);
                                    const canCancel = c.claimant_user_id === user?.id && c.status === "pending_approval";
                                    const canDisburse = isPrivileged && c.status === "approved";
                                    return (
                                        <tr key={c.id} data-testid={`ec-row-${c.id}`}>
                                            <td className="font-mono text-xs">{c.claim_no}</td>
                                            <td>{c.claimant_name}<div className="text-[10px] text-stone-500 capitalize">{c.claimant_role || ""}</div></td>
                                            <td className="text-xs">{c.claim_date}</td>
                                            <td className="text-xs max-w-md truncate" title={c.reason}>{c.reason}</td>
                                            <td className="se-num text-right font-medium">₹ {inr(c.total_amount)}</td>
                                            <td>
                                                <span className="inline-block rounded px-2 py-0.5 text-[10px] font-medium"
                                                      style={{ background: chip.bg, color: chip.fg }}
                                                      data-testid={`ec-status-${c.id}`}>
                                                    {chip.label}
                                                </span>
                                                {c.status === "rejected" && c.rejection_reason ? (
                                                    <div className="text-[10px] text-rose-600 mt-0.5 max-w-xs truncate" title={c.rejection_reason}>
                                                        {c.rejection_reason}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="text-right space-x-1 whitespace-nowrap">
                                                <Button size="sm" variant="ghost" onClick={() => setViewing(c.id)}
                                                        data-testid={`ec-view-${c.id}`}>
                                                    <Eye size={13} />
                                                </Button>
                                                {canEdit ? (
                                                    <Button size="sm" variant="ghost" onClick={() => setEditing(c)}
                                                            data-testid={`ec-edit-${c.id}`}>
                                                        <PencilSimple size={13} />
                                                    </Button>
                                                ) : null}
                                                {canSubmit ? (
                                                    <Button size="sm" onClick={() => submitClaim(c.id, refresh)}
                                                            style={{ background: "#f97316", color: "#fff" }}
                                                            data-testid={`ec-submit-${c.id}`}>
                                                        <PaperPlaneTilt size={13} className="mr-1" /> Submit
                                                    </Button>
                                                ) : null}
                                                {canCancel ? (
                                                    <Button size="sm" variant="outline"
                                                            onClick={() => cancelClaim(c.id, refresh)}
                                                            data-testid={`ec-cancel-${c.id}`}>
                                                        Cancel
                                                    </Button>
                                                ) : null}
                                                {canDisburse ? (
                                                    <Button size="sm" onClick={() => setDisburseTarget(c)}
                                                            style={{ background: "#0f766e", color: "#fff" }}
                                                            data-testid={`ec-disburse-${c.id}`}>
                                                        <CurrencyInr size={13} className="mr-1" /> Reimburse
                                                    </Button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {editing ? (
                editing.status === "pending_approval" ? (
                    // Approver review — mirrors Petty Cash approval UX:
                    // AI-first, verdict-driven, single expense head +
                    // vendor routing panel + reject/approve footer.
                    <ApproveClaimDialog
                        initial={editing}
                        ledgers={ledgers}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); refresh(); }}
                    />
                ) : (
                    // Staff simple form — new claim or fix-and-resubmit of a
                    // rejected claim. Mirrors the Petty Cash "Submit a bill"
                    // flow: just bill + amount + date + note.
                    <SubmitClaimDialog
                        initial={editing}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); refresh(); }}
                    />
                )
            ) : null}
            {viewing ? (
                <ClaimViewDialog
                    cid={viewing}
                    onClose={() => setViewing(null)}
                    onChanged={refresh}
                />
            ) : null}
            {disburseTarget ? (
                <DisburseDialog
                    claim={disburseTarget}
                    ledgers={ledgers}
                    onClose={() => setDisburseTarget(null)}
                    onDone={() => { setDisburseTarget(null); refresh(); }}
                />
            ) : null}
        </>
    );
}

async function submitClaim(cid, refresh) {
    try {
        await api.post(`/expense-claims/${cid}/submit`);
        toast.success("Submitted for approval");
        refresh();
    } catch (e) { toast.error(formatApiError(e)); }
}

async function cancelClaim(cid, refresh) {
    if (!window.confirm("Withdraw this claim back to draft?")) return;
    try {
        await api.post(`/expense-claims/${cid}/cancel`);
        toast.success("Claim withdrawn to draft");
        refresh();
    } catch (e) { toast.error(formatApiError(e)); }
}

/* ---------------------------- Edit / New dialog ---------------------------- */
function SubmitClaimDialog({ initial, onClose, onSaved }) {
    // Simple staff form — mirrors Petty Cash "Submit a bill".
    // Fields: bill (mandatory) + amount + date + note. The approver
    // classifies (line ledgers, GST, vendor routing) during Review.
    const isResubmit = initial?.status === "rejected";
    const [amount, setAmount] = useState(
        isResubmit ? String(initial.total_amount || "") : ""
    );
    const [date, setDate] = useState(
        initial?.claim_date || new Date().toISOString().slice(0, 10)
    );
    const [note, setNote] = useState(initial?.reason || "");
    const [attachment, setAttachment] = useState(initial?.attachment || null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const uploadBill = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/expense-claims/attachment", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachment(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    const submit = async () => {
        const a = Number(amount);
        if (!a || a <= 0) return toast.error("Amount must be positive");
        if (!attachment?.document_id) return toast.error("Attach the bill — proof is mandatory.");
        setSaving(true);
        try {
            // A single unclassified line: the approver will pick the
            // ledger + GST during Review. Ledger left null → backend
            // marks the claim as needs_classification and routes it
            // straight to `pending_approval`.
            const payload = {
                claim_date: date,
                reason: (note && note.trim()) || "Expense claim",
                lines: [{
                    description: (note && note.trim()) || "Expense claim",
                    ledger_id: null,
                    taxable: a,
                    gst_pct: 0,
                    gst_type: null,
                }],
                attachment,
            };
            if (isResubmit) {
                await api.post(`/expense-claims/${initial.id}/resubmit`, payload);
                toast.success("Resubmitted — the accountant will review your bill.");
            } else {
                await api.post("/expense-claims", payload);
                toast.success("Submitted — the accountant will review your bill.");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="ec-submit-dialog">
                <DialogHeader>
                    <DialogTitle>{isResubmit ? "Fix & resubmit claim" : "Submit an expense claim"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {isResubmit && initial?.rejection_reason ? (
                        <div className="border rounded-sm p-3 text-[11px]"
                             style={{ borderColor: "#e11d48", background: "#fef2f2", color: "#991b1b" }}>
                            <b>Rejected — reason:</b> {initial.rejection_reason}
                        </div>
                    ) : null}
                    <div className="text-[11px] text-stone-500">
                        The accountant reviews every claim and does the accounting for you.
                        Just attach the bill + amount and submit — you&apos;ll get reimbursed
                        once it&apos;s approved and disbursed.
                    </div>

                    <div className="border rounded-sm p-3" style={{ borderColor: "var(--se-border)" }}>
                        <Label>Bill (image or PDF) *</Label>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-sm text-xs cursor-pointer hover:bg-stone-50"
                                   style={{ borderColor: "var(--se-border)" }} data-testid="ec-submit-file">
                                <Paperclip size={13} />
                                {uploading ? "Uploading…"
                                 : attachment?.filename ? `Replace: ${attachment.filename}`
                                 : "Attach bill"}
                                <input type="file" accept="image/*,application/pdf" className="hidden"
                                       onChange={(e) => uploadBill(e.target.files?.[0] || null)} />
                            </label>
                            <CameraCaptureButton onFile={uploadBill} label="Camera" testid="ec-submit-camera" />
                            {attachment?.document_id ? (
                                <a href={`${API}/documents/${attachment.document_id}/download`}
                                   target="_blank" rel="noopener noreferrer"
                                   className="text-[11px] underline text-blue-700">view current</a>
                            ) : null}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Amount paid (₹) *</Label>
                            <Input type="number" step="0.01" value={amount}
                                   onChange={(e) => setAmount(e.target.value)}
                                   data-testid="ec-submit-amount" />
                        </div>
                        <div>
                            <Label>Date *</Label>
                            <Input type="date" value={date}
                                   onChange={(e) => setDate(e.target.value)}
                                   data-testid="ec-submit-date" />
                        </div>
                    </div>
                    <div>
                        <Label>Note</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)}
                               placeholder="e.g. Client dinner / Auto for Priya's airport pickup"
                               data-testid="ec-submit-note" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || uploading}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="ec-submit-btn">
                        {saving ? "Submitting…" : (isResubmit ? "Resubmit" : "Submit for approval")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ============================================================================
// ApproveClaimDialog — approver's Review & Classify popup.
// Mirrors Petty Cash's ExpenseDialog UX 1:1 (see PettyCash.jsx):
//   1. Proof + AI auto-rescan-on-open (Reading your bill spinner)
//   2. AI verdict banner (green ITC / neutral Failed / red Mismatch)
//   3. Expense head — auto-suggested if Route A, empty if Route B/C
//   4. GST fields — shown only when isGstBill (no manual toggle)
//   5. Vendor mapping — greyed out for Route A, active with skip
//      checkbox for Route B/C
//   6. Amount + Date + Note
//   7. Footer: Reject / Cancel / Approve & post
// ============================================================================
function ApproveClaimDialog({ initial, ledgers, onClose, onSaved }) {
    const [headLedgerId, setHeadLedgerId] = useState("");
    const [amount, setAmount] = useState(String(initial.total_amount || ""));
    const [note, setNote] = useState(initial.reason || "");
    const [date, setDate] = useState(initial.claim_date || new Date().toISOString().slice(0, 10));
    const [extracting, setExtracting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [homeStateCode, setHomeStateCode] = useState("27");
    const [supplierName, setSupplierName] = useState(initial.supplier_name || "");
    const [supplierGstin, setSupplierGstin] = useState(initial.supplier_gstin || "");
    const [invoiceNo, setInvoiceNo] = useState(initial.invoice_no || "");
    const [invoiceDate, setInvoiceDate] = useState(initial.invoice_date || "");
    const [taxable, setTaxable] = useState(
        initial.taxable_total ? String(initial.taxable_total) : ""
    );
    const [gstRate, setGstRate] = useState("18");
    const [buyerNameOnBill, setBuyerNameOnBill] = useState(initial.buyer_name_on_bill || "");
    const [buyerGstinOnBill, setBuyerGstinOnBill] = useState(initial.buyer_gstin_on_bill || "");
    const [buyerMatchStatus, setBuyerMatchStatus] = useState(initial.buyer_match_status || "");
    const [buyerMatchReason, setBuyerMatchReason] = useState("");
    const [looseConfirmed, setLooseConfirmed] = useState(!!initial.buyer_match_status);
    const [showLooseConfirm, setShowLooseConfirm] = useState(false);
    const [pickedVendorId, setPickedVendorId] = useState(initial.vendor_ledger_id || "");
    const [skipVendorRouting, setSkipVendorRouting] = useState(!!initial.skip_vendor_routing);
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [duplicateWarnings, setDuplicateWarnings] = useState(
        Array.isArray(initial.duplicate_warnings) ? initial.duplicate_warnings : []
    );
    // ITC-confirm gate (Feb 2026) — approver must explicitly tick this before
    // Input GST is claimed. Independent from the OCR-detected `isGstBill`.
    const [itcConfirmed, setItcConfirmed] = useState(!!initial.itc_confirmed);
    // RCM — approver ticks this on an unregistered-GTA / RCM-eligible bill.
    // Disabled automatically if the bill carries GST (mutually exclusive).
    const [rcmApplies, setRcmApplies] = useState(!!initial.rcm_applies);
    const [rcmRate, setRcmRate] = useState(initial.rcm_rate || 5);
    const [aiExtracted, setAiExtracted] = useState(initial.ai_extracted || null);

    const expenseLedgers = useMemo(
        () => (ledgers || [])
            .filter((l) => l.active !== false)
            .filter((l) => ["direct_cost", "indirect_expenses"].includes(l.group_category))
            .filter((l) => l.party_type === "generic")
            .sort((a, b) => (a.group_name || "").localeCompare(b.group_name || "") || a.name.localeCompare(b.name)),
        [ledgers]
    );

    useEffect(() => {
        api.get("/petty-cash/settings/home-state")
            .then(({ data }) => setHomeStateCode(String(data.value || "27")))
            .catch(() => {});
    }, []);

    // Auto-rescan on open — pull the bill from storage, run AI.
    useEffect(() => {
        if (!initial?.id) return;
        if (initial.supplier_name) return;
        if (!(initial.attachment?.document_id)) return;
        (async () => {
            setExtracting(true);
            try {
                const { data } = await api.post(`/expense-claims/${initial.id}/rescan`);
                if (data.error) {
                    toast.error(`AI couldn't read it — ${data.error}. Fill manually.`);
                    return;
                }
                if (data.supplier_name) setSupplierName(data.supplier_name);
                if (data.supplier_gstin) setSupplierGstin(data.supplier_gstin);
                if (data.invoice_no) setInvoiceNo(data.invoice_no);
                if (data.invoice_date) setInvoiceDate(data.invoice_date);
                setBuyerNameOnBill(data.buyer_name_on_bill || "");
                setBuyerGstinOnBill(data.buyer_gstin_on_bill || "");
                setBuyerMatchStatus(data.buyer_match_status || "");
                setBuyerMatchReason(data.buyer_match_reason || "");
                setDuplicateWarnings(Array.isArray(data.duplicate_warnings) ? data.duplicate_warnings : []);
                setItcConfirmed(false);
                setAiExtracted({
                    supplier_name: data.supplier_name || null,
                    supplier_gstin: data.supplier_gstin || null,
                    invoice_no: data.invoice_no || null,
                    invoice_date: data.invoice_date || null,
                    taxable_value: (data.line_seed || {}).taxable ?? null,
                    gst_rate: (data.line_seed || {}).gst_pct ?? null,
                    total: data.total ?? null,
                    buyer_name_on_bill: data.buyer_name_on_bill || null,
                    buyer_gstin_on_bill: data.buyer_gstin_on_bill || null,
                    buyer_match_status: data.buyer_match_status || null,
                    confidence: data.confidence ?? null,
                });
                const seed = data.line_seed || {};
                if (seed.taxable) setTaxable(String(seed.taxable));
                if (seed.gst_pct != null) setGstRate(String(seed.gst_pct));
                if (data.total) setAmount(String(data.total));
                const isGst = data.buyer_match_status === "match_gstin";
                if (isGst && seed.ledger_id) {
                    setHeadLedgerId(seed.ledger_id);
                    if (seed.ledger_name) toast.success(`Head auto-set: ${seed.ledger_name}${seed.ledger_reason ? " · " + seed.ledger_reason : ""}`);
                }
                if (isGst) {
                    setLooseConfirmed(true);
                    toast(`GST bill detected (confidence ${Math.round((data.confidence || 0.7) * 100)}%). Verify numbers, then confirm 2B match to claim ITC.`);
                } else if (data.buyer_match_status === "match_name_loose") {
                    setShowLooseConfirm(true);
                } else if (data.buyer_match_status === "mismatch") {
                    toast.error("⚠ Bill addressed to a different company — ITC cannot be claimed.");
                } else {
                    toast(`Bill read — no GST detected. Pick an expense head manually.`);
                }
            } catch (e) {
                toast.error(`Auto-scan failed: ${formatApiError(e)}. Fill manually.`);
            } finally { setExtracting(false); }
        })();
    }, [initial?.id, initial?.supplier_name, initial?.attachment?.document_id]);

    const isGstBill = buyerMatchStatus === "match_gstin"
        || (buyerMatchStatus === "match_name_loose" && looseConfirmed);
    const supplierState = (supplierGstin || "").trim().slice(0, 2);
    const isIgst = isGstBill && supplierState && supplierState !== homeStateCode;
    const gstDetectionFailed = !extracting
        && !!buyerMatchStatus
        && !isGstBill
        && buyerMatchStatus !== "mismatch";
    // GST Sec 17(5) — the picked expense ledger may be on the block-list.
    const pickedLedger = expenseLedgers.find((l) => l.id === headLedgerId);
    const isBlockedHead = !!(pickedLedger && pickedLedger.blocked_credit);
    const claimsItc = isGstBill && !isBlockedHead;
    const t = Number(taxable) || 0;
    const r = Number(gstRate) || 0;
    const gstAmt = +(t * r / 100).toFixed(2);
    const gross = +(t + gstAmt).toFixed(2);
    const half = +(gstAmt / 2).toFixed(2);
    useEffect(() => {
        if (isGstBill && t > 0) setAmount(String(gross));
    }, [isGstBill, t, gross]);

    const confirmLooseMatch = (yes) => {
        setShowLooseConfirm(false);
        if (yes) {
            setLooseConfirmed(true);
            toast.success("ITC will be claimed based on your confirmation.");
        } else {
            setLooseConfirmed(false);
            setBuyerMatchStatus("mismatch");
            setBuyerMatchReason("You confirmed this bill is not addressed to your company.");
            toast.error("Marked as not addressed to you — approval blocked.");
        }
    };

    const submit = async () => {
        const a = Number(amount);
        if (!headLedgerId) return toast.error("Pick an expense head");
        if (!a || a <= 0) return toast.error("Amount must be positive");
        if (buyerMatchStatus === "mismatch") {
            return toast.error("This bill is not addressed to your company. Cannot approve.");
        }
        if (isGstBill) {
            if (!supplierName.trim()) return toast.error("Supplier name required for GST invoice");
            if (!invoiceNo.trim()) return toast.error("Invoice number required for GST invoice");
            if (!(Number(taxable) > 0)) return toast.error("Taxable value required for GST invoice");
        }
        setSaving(true);
        try {
            // (Sep 2026 policy) Always claim ITC when the line has a GST%.
            // `itcConfirmed` state is retained for legacy compatibility but
            // no longer gates the split.
            const claimItc = isGstBill;
            const payload = {
                claim_date: date,
                reason: (note && note.trim()) || "Expense claim",
                lines: [{
                    description: (note && note.trim()) || "Expense claim",
                    ledger_id: headLedgerId,
                    taxable: Number(taxable),
                    gst_pct: claimItc ? Number(gstRate) : 0,
                    gst_type: claimItc ? (isIgst ? "inter" : "intra") : null,
                }],
                attachment: initial.attachment || null,
                supplier_name: isGstBill ? supplierName : null,
                supplier_gstin: isGstBill ? (supplierGstin || null) : null,
                invoice_no: isGstBill ? invoiceNo : null,
                invoice_date: isGstBill ? (invoiceDate || date) : null,
                buyer_name_on_bill: buyerNameOnBill || null,
                buyer_gstin_on_bill: buyerGstinOnBill || null,
                vendor_ledger_id: pickedVendorId || null,
                skip_vendor_routing: skipVendorRouting,
                itc_confirmed: true,
                // RCM — stripped automatically if the bill carries GST (the
                // backend also enforces the exclusion, this just avoids a
                // round-trip 400).
                rcm_applies: !isGstBill && rcmApplies,
                rcm_rate: !isGstBill && rcmApplies ? Number(rcmRate) : 0,
                ai_extracted: aiExtracted,
            };
            await api.post(`/expense-claims/${initial.id}/approve`, payload);
            toast.success("Approved & classified — awaiting reimbursement");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const doReject = async () => {
        if ((rejectReason || "").trim().length < 4) {
            return toast.error("Please write a reason (min 4 chars) so the claimant can fix it.");
        }
        setSaving(true);
        try {
            await api.post(`/expense-claims/${initial.id}/reject`, { reason: rejectReason.trim() });
            toast.success("Rejected — claimant will see the reason");
            setRejecting(false); setRejectReason("");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ec-approve-dialog">
                <DialogHeader>
                    <DialogTitle>Review &amp; classify {initial.claim_no || ""}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-[11px] text-stone-500">
                        Total to reimburse <b>₹ {inr(Number(amount) || 0)}</b>.
                        Approving posts the expense journal — the claimant then gets paid
                        via <b>To reimburse</b>.
                    </div>

                    <div className="border rounded-sm p-3 relative" style={{ borderColor: "var(--se-border)" }}>
                        <Label>Proof (image or PDF){isGstBill ? " *" : ""}</Label>
                        <div className="mt-1">
                            {initial.attachment?.document_id ? (
                                <div className="flex items-center gap-2 text-xs">
                                    <Paperclip size={13} />
                                    <span className="truncate">{initial.attachment.filename || "attached"}</span>
                                    <a href={`${API}/documents/${initial.attachment.document_id}/download`}
                                       target="_blank" rel="noopener noreferrer"
                                       className="text-[11px] underline text-blue-700">view</a>
                                </div>
                            ) : <div className="text-[11px] text-rose-600">No attachment on file</div>}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-2">
                            The bill is auto-read by AI — supplier, GSTIN, invoice #, taxable, GST rate and best-fit expense head are pre-filled. Check everything before approving.
                        </div>
                        {extracting ? (
                            <div className="absolute inset-0 flex items-center justify-center rounded-sm z-10"
                                 style={{ background: "rgba(255,251,235,0.94)", border: "1px solid #f59e0b" }}
                                 data-testid="ec-approve-extract-loading">
                                <div className="text-center">
                                    <div className="inline-block w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-2" />
                                    <div className="text-sm font-medium text-amber-800">Reading your bill…</div>
                                    <div className="text-[11px] text-amber-700 mt-1">Extracting supplier, GST, expense head. This takes ~10-15s.</div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {isGstBill && isBlockedHead ? (
                        <BlockedCreditBannerEc ledger={pickedLedger} />
                    ) : isGstBill ? (
                        <SanityBannerEc status="match_gstin" reason={buyerMatchReason}
                                        buyerName={buyerNameOnBill} buyerGstin={buyerGstinOnBill} />
                    ) : buyerMatchStatus === "mismatch" ? (
                        <SanityBannerEc status="mismatch" reason={buyerMatchReason}
                                        buyerName={buyerNameOnBill} buyerGstin={buyerGstinOnBill} />
                    ) : gstDetectionFailed ? (
                        <FailedGstDetectBannerEc />
                    ) : null}

                    {/* Layer 2 duplicate-bill warning (soft, cross-module) */}
                    <DuplicateWarningBanner matches={duplicateWarnings} testid="ec-duplicate-warning" />

                    <div>
                        <Label>Expense head *</Label>
                        <ComboSelect
                            value={headLedgerId}
                            onValueChange={setHeadLedgerId}
                            options={expenseLedgers.map((l) => ({
                                id: l.id, name: l.name,
                                hint: l.group_name && l.group_name !== l.name ? l.group_name : undefined,
                            }))}
                            placeholder="Search: software, rent, fuel…"
                            testId="ec-approve-head"
                        />
                    </div>

                    {isGstBill ? (
                        <div className="grid grid-cols-2 gap-4 border rounded-sm p-4"
                             style={{ borderColor: "var(--se-border)", background: "#fffbeb" }}>
                            <div className="col-span-2"><Label>Supplier name *</Label>
                                <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} data-testid="ec-gst-supplier" /></div>
                            <div><Label>Supplier GSTIN</Label>
                                <Input value={supplierGstin} onChange={(e) => setSupplierGstin(e.target.value.toUpperCase())} data-testid="ec-gst-gstin" maxLength={15} />
                                {supplierGstin && supplierGstin.length === 15 ? (
                                    <div className="text-[10px] mt-1" style={{ color: isIgst ? "#0d6efd" : "#0f766e" }}>
                                        {isIgst ? `Inter-state (from ${supplierState} → ${homeStateCode}) — Input IGST` : `Intra-state (both ${supplierState}) — Input CGST + SGST`}
                                    </div>
                                ) : null}
                            </div>
                            <div><Label>Invoice # *</Label>
                                <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} data-testid="ec-gst-inv-no" /></div>
                            <div><Label>Invoice date</Label>
                                <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="ec-gst-inv-date" /></div>
                            <div><Label>Taxable value (₹) *</Label>
                                <Input type="number" step="0.01" value={taxable} onChange={(e) => setTaxable(e.target.value)} data-testid="ec-gst-taxable" /></div>
                            <div><Label>GST rate</Label>
                                <select className="w-full border rounded-sm px-3 py-2 text-sm" value={gstRate}
                                        onChange={(e) => setGstRate(e.target.value)} data-testid="ec-gst-rate">
                                    {[0, 5, 12, 18, 28].map((rate) => <option key={rate} value={rate}>{rate} %</option>)}
                                </select>
                            </div>
                            {t > 0 ? (
                                <div className="col-span-2 border-t pt-3 mt-1 text-xs" style={{ borderColor: "var(--se-border)" }}>
                                    {/* ITC-confirm gate REMOVED (Sep 2026) —
                                        always split Input CGST/SGST/IGST.
                                        2B mismatches → notification only. */}
                                    <div className="font-medium mb-1">Journal preview{isBlockedHead ? " · ITC blocked (17(5))" : ""}</div>
                                    <div className="font-mono text-[11px] space-y-0.5">
                                        {isBlockedHead ? (
                                            <>
                                                <div>Dr <span className="text-stone-700">{expenseLedgers.find((l) => l.id === headLedgerId)?.name || "—"}</span> · ₹ {inr(gross)} <span className="text-amber-700">(gross — Sec 17(5))</span></div>
                                                <div>Cr <span className="text-stone-700">{initial.claimant_name || "Claimant"}</span> · ₹ {inr(gross)}</div>
                                            </>
                                        ) : (
                                            <>
                                                <div>Dr <span className="text-stone-700">{expenseLedgers.find((l) => l.id === headLedgerId)?.name || "—"}</span> · ₹ {inr(t)}</div>
                                                {isIgst
                                                    ? <div>Dr <span className="text-stone-700">Input IGST</span> · ₹ {inr(gstAmt)}</div>
                                                    : gstAmt > 0 ? <>
                                                        <div>Dr <span className="text-stone-700">Input CGST</span> · ₹ {inr(gstAmt - half)}</div>
                                                        <div>Dr <span className="text-stone-700">Input SGST</span> · ₹ {inr(half)}</div>
                                                    </> : null}
                                                <div>Cr <span className="text-stone-700">{initial.claimant_name || "Claimant"}</span> · ₹ {inr(gross)}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <VendorRoutingPanelEc
                        isGstBill={isGstBill}
                        supplierName={supplierName}
                        supplierGstin={supplierGstin}
                        ledgers={ledgers}
                        pickedVendorId={pickedVendorId}
                        setPickedVendorId={setPickedVendorId}
                        skipVendorRouting={skipVendorRouting}
                        setSkipVendorRouting={setSkipVendorRouting}
                    />

                    <RcmTogglePanel
                        isGstBill={isGstBill}
                        ledgers={ledgers}
                        pickedVendorId={pickedVendorId}
                        rcmApplies={rcmApplies}
                        setRcmApplies={setRcmApplies}
                        rcmRate={rcmRate}
                        setRcmRate={setRcmRate}
                        taxable={taxable}
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Amount paid (₹) *{isGstBill ? <span className="text-[10px] text-stone-500"> (auto)</span> : null}</Label>
                            <Input type="number" step="0.01" value={amount}
                                   onChange={(e) => setAmount(e.target.value)}
                                   readOnly={isGstBill} data-testid="ec-approve-amount" />
                        </div>
                        <div>
                            <Label>Date *</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ec-approve-date" />
                        </div>
                    </div>
                    <div>
                        <Label>Note</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)}
                               placeholder="e.g. Client dinner / Auto for airport"
                               data-testid="ec-approve-note" />
                    </div>
                </div>
                <DialogFooter>
                    {!rejecting ? (
                        <Button variant="outline" onClick={() => setRejecting(true)}
                                style={{ color: "#b91c1c", borderColor: "#b91c1c" }}
                                data-testid="ec-approve-reject-open">
                            Reject
                        </Button>
                    ) : null}
                    {rejecting ? (
                        <div className="flex-1 flex items-center gap-2">
                            <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                                   placeholder="Reason (visible to claimant)"
                                   className="flex-1 h-9"
                                   data-testid="ec-approve-reject-reason" />
                            <Button size="sm" variant="outline" onClick={() => { setRejecting(false); setRejectReason(""); }}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={doReject} disabled={saving}
                                    style={{ background: "#b91c1c", color: "#fff" }}
                                    data-testid="ec-approve-reject-confirm">
                                Confirm reject
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                            <Button onClick={submit}
                                    disabled={saving || buyerMatchStatus === "mismatch"}
                                    style={{ background: buyerMatchStatus === "mismatch" ? "#a8a29e" : "#f59e0b", color: "#fff" }}
                                    data-testid="ec-approve-submit">
                                {saving ? "Saving…"
                                 : buyerMatchStatus === "mismatch" ? "Blocked (bill isn't yours)"
                                 : "Approve & post"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
            <LooseMatchConfirmDialogEc open={showLooseConfirm} onConfirm={confirmLooseMatch}
                                       buyerName={buyerNameOnBill} buyerGstin={buyerGstinOnBill} />
        </Dialog>
    );
}

// --- Local review-popup helpers (parallel to PettyCash.jsx) ---
function SanityBannerEc({ status, reason, buyerName, buyerGstin }) {
    const styles = {
        match_gstin: { bg: "#f0fdf4", border: "#86efac", fg: "#166534", icon: "✓", label: "ITC auto-claimed — buyer GSTIN matches" },
        mismatch:    { bg: "#fef2f2", border: "#fca5a5", fg: "#991b1b", icon: "⚠", label: "This bill isn't addressed to your company — approval blocked" },
    };
    const s = styles[status] || styles.match_gstin;
    return (
        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="ec-sanity-banner"
             style={{ background: s.bg, borderColor: s.border, color: s.fg }}>
            <div className="font-medium flex items-center gap-2"><span aria-hidden="true">{s.icon}</span> {s.label}</div>
            {reason ? <div className="text-[11px] mt-1 opacity-90">{reason}</div> : null}
            {(buyerName || buyerGstin) ? (
                <div className="text-[11px] mt-1 opacity-80 font-mono">
                    Bill Bill-To: {buyerName || "(name missing)"} · GSTIN: {buyerGstin || "(GSTIN missing)"}
                </div>
            ) : null}
        </div>
    );
}


function BlockedCreditBannerEc({ ledger }) {
    return (
        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="ec-blocked-credit-banner"
             style={{ background: "#fef2f2", borderColor: "#fca5a5", color: "#991b1b" }}>
            <div className="font-medium flex items-center gap-2">
                <span aria-hidden="true">🚫</span> Blocked credit — Sec {ledger?.blocked_credit_section || "17(5)"}
            </div>
            <div className="text-[11px] mt-1 opacity-90">
                <b>{ledger?.blocked_credit_reason || "Ineligible ITC"}</b> — the GST amount will be
                booked gross into the expense head. No Input GST will be claimed on this bill.
            </div>
        </div>
    );
}

function FailedGstDetectBannerEc() {
    return (
        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="ec-gst-detect-failed"
             style={{ background: "#f8fafc", borderColor: "#cbd5e1", color: "#334155" }}>
            <div className="font-medium flex items-center gap-2"><span aria-hidden="true">ℹ</span> Failed to detect GST bill</div>
            <div className="text-[11px] mt-1 opacity-90">
                The AI could not confirm both the supplier&apos;s GSTIN and your company&apos;s
                GSTIN on this bill — treating it as a non-GST claim. Pick an expense
                head (mandatory) and optionally a vendor below.
            </div>
        </div>
    );
}

function VendorRoutingPanelEc({ isGstBill, supplierName, supplierGstin,
                                ledgers, pickedVendorId, setPickedVendorId,
                                skipVendorRouting, setSkipVendorRouting }) {
    const vendorOptions = useMemo(() =>
        (ledgers || [])
            // Transporters are vendors with special RCM/GTA tax treatment —
            // include them in the vendor picker so a cash-paid transport
            // bill can be routed to its ledger just like any other vendor.
            .filter((l) => ["vendor", "transporter"].includes(l.party_type) && l.active !== false)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((l) => ({
                id: l.id, name: l.name,
                hint: l.party_type === "transporter"
                    ? `TRANSPORT · ${l.group_name || ""}`.trim()
                    : l.group_name,
            })),
    [ledgers]);

    if (isGstBill) {
        return (
            <div className="border rounded-sm p-3 flex items-start gap-3 opacity-70 pointer-events-none"
                 style={{ borderColor: "var(--se-border)", background: "#f5f5f4" }}
                 data-testid="ec-vendor-auto-disabled">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ background: "#0f766e", color: "#fff", fontSize: 14 }}>✓</div>
                <div className="flex-1">
                    <div className="text-sm font-medium text-stone-700">Vendor ledger auto-routed</div>
                    <div className="text-[11px] text-stone-600 mt-0.5">
                        This is a valid GST bill — <b>{supplierName || "the supplier"}</b>
                        {supplierGstin ? ` (${supplierGstin})` : ""} will be booked into
                        their vendor ledger automatically. No action needed here.
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="border rounded-sm p-3"
             style={{ borderColor: "#f59e0b", background: "#fffbeb" }}
             data-testid="ec-vendor-picker">
            <div className="text-sm font-medium text-amber-900 mb-1">Map to a vendor?</div>
            <div className="text-[11px] text-amber-800 mb-2">
                Pick an existing vendor to route this expense through their ledger. If it&apos;s a
                small cash memo with no vendor to track, tick <b>Skip vendor mapping</b> — the
                amount will land straight in the expense head.
            </div>
            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <ComboSelect value={pickedVendorId} onValueChange={setPickedVendorId}
                                 options={vendorOptions}
                                 placeholder={vendorOptions.length ? "Search vendors…" : "No vendors yet"}
                                 size="sm" disabled={skipVendorRouting}
                                 testId="ec-vendor-select" />
                </div>
            </div>
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="ec-vendor-skip-wrap">
                <input type="checkbox" checked={skipVendorRouting}
                       onChange={(e) => {
                           setSkipVendorRouting(e.target.checked);
                           if (e.target.checked) setPickedVendorId("");
                       }}
                       className="cursor-pointer" data-testid="ec-vendor-skip" />
                <span className="text-[11px] text-amber-900">
                    Skip vendor mapping for this invoice (book as a direct expense)
                </span>
            </label>
        </div>
    );
}


/**
 * RCM toggle — only reachable on a non-GST bill. When the picked vendor is a
 * transporter with the RCM-default flag turned on, we auto-tick RCM at 5%
 * (the GTA inward-freight rate) and leave the approver free to override.
 */
function RcmTogglePanel({ isGstBill, ledgers, pickedVendorId, rcmApplies, setRcmApplies, rcmRate, setRcmRate, taxable }) {
    const pickedVendor = useMemo(
        () => (ledgers || []).find((l) => l.id === pickedVendorId) || null,
        [ledgers, pickedVendorId],
    );
    const isTransporter = pickedVendor?.party_type === "transporter";
    // Auto-tick when a transporter vendor with RCM-default is chosen (and the
    // bill is non-GST). Approver can untick it manually.
    useEffect(() => {
        if (!isGstBill && isTransporter && pickedVendor?.inward_transport_rcm && !rcmApplies) {
            setRcmApplies(true);
            setRcmRate(5);
        }
    }, [isGstBill, isTransporter, pickedVendor?.inward_transport_rcm]); // eslint-disable-line

    if (isGstBill) {
        return (
            <div className="border rounded-sm p-3 flex items-start gap-3 opacity-70 pointer-events-none"
                 style={{ borderColor: "var(--se-border)", background: "#f5f5f4" }}
                 data-testid="ec-rcm-disabled">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[11px]"
                     style={{ background: "#64748b", color: "#fff" }}>RCM</div>
                <div className="flex-1">
                    <div className="text-sm font-medium text-stone-700">RCM not applicable</div>
                    <div className="text-[11px] text-stone-600 mt-0.5">
                        This bill already carries GST — RCM and GST on the same bill are
                        mutually exclusive. If this really is an RCM bill, clear the GST%
                        on all lines first.
                    </div>
                </div>
            </div>
        );
    }

    const rcmAmount = rcmApplies && rcmRate > 0
        ? Math.round(Number(taxable || 0) * Number(rcmRate) / 100 * 100) / 100
        : 0;

    return (
        <div className="border rounded-sm p-3"
             style={{ borderColor: rcmApplies ? "#7c3aed" : "var(--se-border)", background: rcmApplies ? "#f5f3ff" : "#fafafa" }}
             data-testid="ec-rcm-panel">
            <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={rcmApplies}
                       onChange={(e) => setRcmApplies(e.target.checked)}
                       className="mt-0.5 cursor-pointer"
                       data-testid="ec-rcm-toggle" />
                <div className="flex-1">
                    <div className="text-sm font-medium">
                        Reverse Charge Mechanism (RCM) applies on this bill
                        {isTransporter ? <span className="ml-2 se-chip se-chip-plum text-[9px]">TRANSPORT</span> : null}
                    </div>
                    <div className="text-[11px] text-stone-600 mt-0.5">
                        Vendor didn&apos;t charge GST — we self-pay it and claim ITC back
                        in the same journal. Net cash impact: zero. Common cases:
                        unregistered Goods Transport Agency bills (5%) or rent to an
                        unregistered landlord (18%).
                    </div>
                </div>
            </label>
            {rcmApplies ? (
                <div className="flex items-center gap-3 mt-3 pl-6">
                    <Label className="text-[10px] mb-0">Rate</Label>
                    <select value={rcmRate} onChange={(e) => setRcmRate(Number(e.target.value))}
                            className="h-8 border rounded-sm px-2 text-xs"
                            style={{ borderColor: "var(--se-border)" }}
                            data-testid="ec-rcm-rate">
                        <option value={5}>5% (Goods transport)</option>
                        <option value={12}>12%</option>
                        <option value={18}>18% (Rent / Legal / Other)</option>
                    </select>
                    <div className="text-[11px] text-purple-700 font-mono">
                        → RCM Payable ₹ {inr(rcmAmount)} / RCM ITC ₹ {inr(rcmAmount)}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function LooseMatchConfirmDialogEc({ open, onConfirm, buyerName, buyerGstin }) {
    return (
        <Dialog open={open} onOpenChange={() => onConfirm(false)}>
            <DialogContent className="max-w-md" data-testid="ec-loose-confirm">
                <DialogHeader><DialogTitle>Is this bill addressed to your company?</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="text-stone-600">
                        The AI found a Bill-To section but it&apos;s not a GSTIN we could match. It could still be your bill.
                    </div>
                    <div className="border rounded-sm p-3 font-mono text-xs" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                        <div><span className="text-stone-500">Buyer name on bill: </span><b>{buyerName || "—"}</b></div>
                        <div><span className="text-stone-500">GSTIN on bill: </span><b>{buyerGstin || "—"}</b></div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onConfirm(false)} data-testid="ec-loose-no">No, not my bill</Button>
                    <Button onClick={() => onConfirm(true)} style={{ background: "#0d6efd", color: "#fff" }} data-testid="ec-loose-yes">
                        Yes, claim ITC
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}



function ClaimEditDialog({ initial, ledgers, onClose, onSaved, mode = "submit" }) {
    // `mode`: "submit" — staff filing a fresh claim (or resubmit after reject),
    //         "review" — approver classifying an existing pending_approval claim.
    // In submit mode we hide the ledger + GST fields entirely; staff just
    // attaches the bill, writes a reason, and enters an amount per receipt.
    // The approver classifies during review.
    const simple = mode === "submit";
    const isNew = !!initial.new;
    const [claimDate, setClaimDate] = useState(initial.claim_date || new Date().toISOString().slice(0, 10));
    const [reason, setReason] = useState(initial.reason || "");
    const [attachment, setAttachment] = useState(initial.attachment || null);
    // Bill-level GST audit fields — populated by /extract, verified server-side
    // on save so a wrong-buyer ITC claim can't sneak through.
    const [supplierName, setSupplierName] = useState(initial.supplier_name || "");
    const [supplierGstin, setSupplierGstin] = useState(initial.supplier_gstin || "");
    const [invoiceNo, setInvoiceNo] = useState(initial.invoice_no || "");
    const [invoiceDate, setInvoiceDate] = useState(initial.invoice_date || "");
    const [buyerNameOnBill, setBuyerNameOnBill] = useState(initial.buyer_name_on_bill || "");
    const [buyerGstinOnBill, setBuyerGstinOnBill] = useState(initial.buyer_gstin_on_bill || "");
    const [buyerMatchStatus, setBuyerMatchStatus] = useState(initial.buyer_match_status || "");
    const [buyerMatchReason, setBuyerMatchReason] = useState(initial.buyer_match_reason || "");
    const [lines, setLines] = useState(
        (initial.lines && initial.lines.length ? initial.lines : [{}]).map((l) => ({
            description: l.description || "",
            ledger_id: l.ledger_id || "",
            taxable: l.taxable ?? "",
            gst_pct: l.gst_pct ?? 0,
            gst_type: l.gst_type || "",
        }))
    );
    const [busy, setBusy] = useState(false);
    const [extracting, setExtracting] = useState(false);

    // Expense-side ledgers — anything under a group whose category hits the
    // P&L expense line (`direct_cost` or `indirect_expenses`). The old
    // filter looked for "expense" in the group NAME which excluded
    // legitimate heads like "Software", "Rent", "Advertising", "Travel &
    // Conveyance", etc. — leaving employees stuck with no way to map their
    // claim. `group_category` comes back from `/api/ledgers` now.
    const expenseLedgers = useMemo(
        () => (ledgers || [])
            .filter((l) => l.active !== false)
            .filter((l) => ["direct_cost", "indirect_expenses"].includes(l.group_category))
            .filter((l) => l.party_type === "generic")
            // Hide auto-managed expense-fiat ledgers (Salaries — Fiat etc.) —
            // those are for internal system journals only.
            .filter((l) => !l.system),
        [ledgers]
    );

    const totals = useMemo(() => {
        let taxable = 0, gst = 0;
        for (const l of lines) {
            const t = Number(l.taxable || 0);
            const g = t * Number(l.gst_pct || 0) / 100;
            taxable += t; gst += g;
        }
        return { taxable, gst, total: taxable + gst };
    }, [lines]);

    const addLine = () => setLines((ls) => [...ls, {}]);
    const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
    const editLine = (i, patch) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));

    const uploadProof = async (file) => {
        if (!file) return;
        setExtracting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Auto-extract on upload — this endpoint saves the file server-side
            // and returns the pre-filled fields in one round-trip.
            const { data } = await api.post("/expense-claims/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachment(data.attachment || null);
            if (data.error) {
                toast.error(`AI couldn't read it — ${data.error}. Fill the details manually.`);
                return;
            }
            // Persist bill-level GST audit fields — the server re-verifies
            // these on save, so we MUST carry them through even if the user
            // doesn't edit them.
            setSupplierName(data.supplier_name || "");
            setSupplierGstin(data.supplier_gstin || "");
            setInvoiceNo(data.invoice_no || "");
            setInvoiceDate(data.invoice_date || "");
            setBuyerNameOnBill(data.buyer_name_on_bill || "");
            setBuyerGstinOnBill(data.buyer_gstin_on_bill || "");
            setBuyerMatchStatus(data.buyer_match_status || "");
            setBuyerMatchReason(data.buyer_match_reason || "");
            // Seed the reason from supplier / invoice info if it's still blank
            const parts = [];
            if (data.supplier_name) parts.push(data.supplier_name);
            if (data.invoice_no) parts.push(`#${data.invoice_no}`);
            if (!reason.trim() && parts.length) setReason(parts.join(" · "));
            // Seed the first line with the AI-suggested ledger + taxable + GST
            const seed = data.line_seed || {};
            if (seed.ledger_id || seed.taxable) {
                setLines((prev) => {
                    const next = [...prev];
                    next[0] = {
                        description: seed.description || next[0]?.description || "",
                        ledger_id: seed.ledger_id || next[0]?.ledger_id || "",
                        taxable: seed.taxable != null ? String(seed.taxable) : (next[0]?.taxable ?? ""),
                        gst_pct: seed.gst_pct ?? 0,
                        gst_type: seed.gst_type || "",
                    };
                    return next;
                });
                if (seed.ledger_name && seed.ledger_reason) {
                    toast.success(`Head auto-set: ${seed.ledger_name} · ${seed.ledger_reason}`);
                }
            }
            if (data.buyer_match_status === "mismatch") {
                toast.error(`⚠ Bill addressed to a different company — ITC cannot be claimed. ${data.buyer_match_reason || ""}`);
            } else {
                toast.success(`Read the bill (confidence ${Math.round((data.confidence || 0.7) * 100)}%). Verify before submitting.`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setExtracting(false); }
    };

    // On approver review of a dumb-submitted claim, auto-run rescan so the
    // supplier / GST / line-seed data is populated before they pick a head.
    // Skip if the claim already has supplier_name (extract ran at submit).
    useEffect(() => {
        if (mode !== "review") return;
        if (!initial?.id) return;
        if (initial.supplier_name) return;             // already extracted
        if (!(initial.attachment?.document_id)) return; // no bill to scan
        (async () => {
            setExtracting(true);
            try {
                const { data } = await api.post(`/expense-claims/${initial.id}/rescan`);
                if (data.error) {
                    toast.error(`AI couldn't read it — ${data.error}. Fill the details manually.`);
                    return;
                }
                setSupplierName(data.supplier_name || "");
                setSupplierGstin(data.supplier_gstin || "");
                setInvoiceNo(data.invoice_no || "");
                setInvoiceDate(data.invoice_date || "");
                setBuyerNameOnBill(data.buyer_name_on_bill || "");
                setBuyerGstinOnBill(data.buyer_gstin_on_bill || "");
                setBuyerMatchStatus(data.buyer_match_status || "");
                setBuyerMatchReason(data.buyer_match_reason || "");
                const seed = data.line_seed || {};
                // Only auto-fill the ledger + GST when this is a confirmed
                // GST bill (buyer GSTIN matches ours). If GST detection
                // failed, keep the ledger empty so the approver must pick.
                const isGst = data.buyer_match_status === "match_gstin";
                if (isGst && (seed.ledger_id || seed.taxable)) {
                    setLines((prev) => {
                        const next = [...prev];
                        next[0] = {
                            description: seed.description || next[0]?.description || "",
                            ledger_id: seed.ledger_id || next[0]?.ledger_id || "",
                            taxable: seed.taxable != null ? String(seed.taxable) : (next[0]?.taxable ?? ""),
                            gst_pct: seed.gst_pct ?? 0,
                            gst_type: seed.gst_type || "",
                        };
                        return next;
                    });
                    if (seed.ledger_name) toast.success(`Head auto-set: ${seed.ledger_name}${seed.ledger_reason ? " · " + seed.ledger_reason : ""}`);
                } else if (!isGst && seed.taxable) {
                    // Non-GST bill — still seed the amount but LEAVE the
                    // ledger empty so the approver consciously classifies.
                    setLines((prev) => {
                        const next = [...prev];
                        next[0] = {
                            description: seed.description || next[0]?.description || "",
                            ledger_id: "",
                            taxable: String(seed.taxable),
                            gst_pct: 0,
                            gst_type: "",
                        };
                        return next;
                    });
                }
                if (data.buyer_match_status === "mismatch") {
                    toast.error(`⚠ Bill addressed to a different company — ITC cannot be claimed.`);
                } else if (isGst) {
                    toast.success(`GST bill confirmed — ITC will be claimed (confidence ${Math.round((data.confidence || 0.7) * 100)}%).`);
                } else {
                    toast(`Bill read (confidence ${Math.round((data.confidence || 0.7) * 100)}%) — no GST detected. Pick an expense head manually.`);
                }
            } catch (e) {
                toast.error(`Auto-scan failed: ${formatApiError(e)}. Fill the details manually.`);
            } finally { setExtracting(false); }
        })();
    }, [mode, initial?.id]);

    const save = async (thenSubmit = false) => {
        // Validation
        if (!reason.trim()) return toast.error("Add a short reason for the claim");
        if (!claimDate) return toast.error("Pick the date you paid");
        for (const [i, l] of lines.entries()) {
            if (!l.description?.trim()) return toast.error(`Line ${i + 1}: add a description`);
            if (!l.ledger_id) return toast.error(`Line ${i + 1}: pick an expense head`);
            if (!(Number(l.taxable) > 0)) return toast.error(`Line ${i + 1}: taxable amount must be > 0`);
        }
        if (thenSubmit && !attachment) return toast.error("Attach the proof before submitting");
        const payload = {
            claim_date: claimDate,
            reason: reason.trim(),
            lines: lines.map((l) => ({
                description: l.description.trim(),
                ledger_id: l.ledger_id,
                taxable: Number(l.taxable),
                gst_pct: Number(l.gst_pct || 0),
                gst_type: (l.gst_pct > 0 ? (l.gst_type || "intra") : null),
            })),
            attachment: attachment || null,
            // Bill-level GST audit — sent so the server can re-verify the
            // buyer sanity check and reject a wrong-buyer ITC claim.
            supplier_name: supplierName || null,
            supplier_gstin: supplierGstin || null,
            invoice_no: invoiceNo || null,
            invoice_date: invoiceDate || null,
            buyer_name_on_bill: buyerNameOnBill || null,
            buyer_gstin_on_bill: buyerGstinOnBill || null,
        };
        setBusy(true);
        try {
            let cid = initial.id;
            if (simple && mode === "submit") {
                // Simplified staff flow — resubmit if we're editing a rejected
                // claim, otherwise create fresh. Skip the classic draft->submit
                // dance; simplified submissions go straight to pending_approval.
                if (initial.status === "rejected") {
                    await api.post(`/expense-claims/${cid}/resubmit`, payload);
                    toast.success("Resubmitted — the accountant will review your bill.");
                } else {
                    await api.post("/expense-claims", payload);
                    toast.success("Submitted — the accountant will review your bill.");
                }
            } else if (mode === "review") {
                // Approver classifying — approve endpoint accepts the full
                // classification payload (line ledgers + GST fields).
                await api.post(`/expense-claims/${cid}/approve`, payload);
                toast.success("Approved & classified");
            } else if (isNew) {
                const { data } = await api.post("/expense-claims", payload);
                cid = data.id;
                toast.success("Saved as draft");
                if (thenSubmit) {
                    await api.post(`/expense-claims/${cid}/submit`);
                    toast.success("Submitted for approval");
                }
            } else {
                await api.patch(`/expense-claims/${cid}`, payload);
                toast.success("Updated");
                if (thenSubmit) {
                    await api.post(`/expense-claims/${cid}/submit`);
                    toast.success("Submitted for approval");
                }
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="w-[95vw] max-w-5xl max-h-[92vh] overflow-y-auto sm:w-full" data-testid="ec-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        {mode === "review" ? `Review & classify ${initial.claim_no || ""}`
                            : (initial.status === "rejected" ? `Fix & resubmit ${initial.claim_no || ""}`
                                : (isNew ? "New expense claim" : `Edit ${initial.claim_no}`))}
                    </DialogTitle>
                    {simple ? (
                        <div className="text-[11px] text-stone-500 pt-1">
                            {initial.status === "rejected" && initial.rejection_reason ? (
                                <div className="mb-2 p-2 rounded-sm text-[11px]"
                                     style={{ background: "#fef2f2", border: "1px solid #e11d48", color: "#991b1b" }}>
                                    <b>Rejected — reason:</b> {initial.rejection_reason}
                                </div>
                            ) : null}
                            The accountant reviews every claim and does the accounting for you. Just attach the bill, jot a short description + amount and submit.
                        </div>
                    ) : null}
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Date paid</Label>
                            <Input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)}
                                   data-testid="ec-edit-date" />
                        </div>
                        <div>
                            <Label>Attachment (proof)</Label>
                            <div className="relative">
                                <AttachmentField attachment={attachment} onUpload={uploadProof}
                                                  onRemove={() => setAttachment(null)} />
                                {extracting ? (
                                    <div className="absolute inset-0 flex items-center justify-center rounded-sm z-10"
                                         style={{ background: "rgba(255,251,235,0.94)", border: "1px solid #f59e0b" }}
                                         data-testid="ec-extract-loading">
                                        <div className="text-center">
                                            <div className="inline-block w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-1" />
                                            <div className="text-xs font-medium text-amber-800">Reading your bill…</div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                            <div className="text-[10px] text-stone-500 mt-1">
                                Drop a bill and we&apos;ll auto-fill supplier, GST, expense head, and total.
                            </div>
                        </div>
                    </div>

                    {/* AI verdict — shown only after extract has run. Route A =
                        ITC auto-claimed. Anything else that isn't a mismatch =
                        "Failed to detect GST bill" (approver picks head manually). */}
                    {!extracting && buyerMatchStatus === "match_gstin" ? (
                        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="ec-itc-banner"
                             style={{ background: "#f0fdf4", borderColor: "#86efac", color: "#166534" }}>
                            <div className="font-medium flex items-center gap-2">
                                <span aria-hidden="true">✓</span> ITC auto-claimed — buyer GSTIN matches
                            </div>
                            <div className="text-[11px] mt-1 opacity-90">{buyerMatchReason}</div>
                            {(buyerNameOnBill || buyerGstinOnBill) ? (
                                <div className="text-[11px] mt-1 opacity-80 font-mono">
                                    Bill-To: {buyerNameOnBill || "(name missing)"} · GSTIN: {buyerGstinOnBill || "(GSTIN missing)"}
                                </div>
                            ) : null}
                        </div>
                    ) : !extracting && buyerMatchStatus === "mismatch" ? (
                        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="ec-mismatch-banner"
                             style={{ background: "#fef2f2", borderColor: "#fca5a5", color: "#991b1b" }}>
                            <div className="font-medium flex items-center gap-2">
                                <span aria-hidden="true">⚠</span> This bill isn&apos;t addressed to your company — submission blocked
                            </div>
                            {buyerMatchReason ? <div className="text-[11px] mt-1 opacity-90">{buyerMatchReason}</div> : null}
                        </div>
                    ) : !extracting && buyerMatchStatus ? (
                        <div className="border rounded-sm px-4 py-3 text-sm" data-testid="ec-gst-detect-failed"
                             style={{ background: "#f8fafc", borderColor: "#cbd5e1", color: "#334155" }}>
                            <div className="font-medium flex items-center gap-2">
                                <span aria-hidden="true">ℹ</span> Failed to detect GST bill
                            </div>
                            <div className="text-[11px] mt-1 opacity-90">
                                The AI could not confirm both the supplier&apos;s GSTIN and your company&apos;s
                                GSTIN on this bill — treating it as a non-GST claim. Pick an expense
                                head (mandatory) for each line below.
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <Label>Reason / description</Label>
                        <Textarea value={reason} rows={2}
                                  onChange={(e) => setReason(e.target.value)}
                                  placeholder="e.g. Client dinner + auto to & from airport"
                                  data-testid="ec-edit-reason" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>Line items</Label>
                            <Button size="sm" variant="outline" onClick={addLine} data-testid="ec-add-line">
                                <Plus size={12} className="mr-1" /> Add line
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {lines.map((l, i) => (
                                <LineRow key={i}
                                    line={l} idx={i}
                                    expenseLedgers={expenseLedgers}
                                    onChange={(patch) => editLine(i, patch)}
                                    onRemove={lines.length > 1 ? () => removeLine(i) : null}
                                    simple={simple}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="rounded-md p-3 flex items-center justify-between text-sm"
                         style={{ background: "#fafaf9", border: "1px solid var(--se-border)" }}>
                        <div className="text-stone-600">
                            <span className="mr-4">Taxable: <b>₹ {inr(totals.taxable)}</b></span>
                            <span>GST: <b>₹ {inr(totals.gst)}</b></span>
                        </div>
                        <div className="text-lg font-medium" style={{ color: "var(--se-brand)" }}>
                            Total ₹ {inr(totals.total)}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    {mode === "review" ? (
                        <Button onClick={() => save(false)} disabled={busy || !attachment}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="ec-approve-classify">
                            {busy ? "…" : "Approve & classify"}
                        </Button>
                    ) : simple ? (
                        <Button onClick={() => save(false)} disabled={busy || !attachment}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="ec-submit-simple">
                            {busy ? "…" : (initial.status === "rejected" ? "Resubmit" : "Submit for approval")}
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => save(false)} disabled={busy}
                                    data-testid="ec-save-draft">
                                Save draft
                            </Button>
                            <Button onClick={() => save(true)} disabled={busy || !attachment}
                                    style={{ background: "var(--se-brand)", color: "#fff" }}
                                    data-testid="ec-save-submit">
                                {busy ? "…" : "Save & submit"}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function LineRow({ line, idx, expenseLedgers, onChange, onRemove, simple = false }) {
    // simple=true → staff mode: only description + amount visible (no ledger, no GST).
    // simple=false → approver mode: full classification row (ledger + GST).
    const gstPreview = (() => {
        const t = Number(line.taxable || 0);
        const p = Number(line.gst_pct || 0);
        return p > 0 && t > 0 ? (t * p / 100) : 0;
    })();
    const total = Number(line.taxable || 0) + gstPreview;
    const headOptions = useMemo(
        () => expenseLedgers.map((l) => ({
            id: l.id,
            name: l.name,
            hint: l.group_name && l.group_name !== l.name ? l.group_name : undefined,
        })),
        [expenseLedgers]
    );
    return (
        <div className="rounded-md border p-3 space-y-2 bg-stone-50/40"
             style={{ borderColor: "var(--se-border)" }}
             data-testid={`ec-line-${idx}`}>
            <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider min-w-[54px]">Line {idx + 1}</span>
                <Input value={line.description || ""} placeholder="What was this line for? (e.g. Clappia annual plan, Airport pickup…)"
                       onChange={(e) => onChange({ description: e.target.value })}
                       data-testid={`ec-line-${idx}-desc`}
                       className="flex-1 h-9" />
                {onRemove ? (
                    <Button size="sm" variant="ghost" onClick={onRemove}
                            className="hover:text-rose-600 h-9 w-9 p-0"
                            data-testid={`ec-remove-line-${idx}`}
                            title="Remove this line">
                        <Trash size={13} />
                    </Button>
                ) : null}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_130px_110px_130px] gap-2 items-start" style={{ display: simple ? "grid" : undefined, gridTemplateColumns: simple ? "1fr 130px" : undefined }}>
                {simple ? null : (
                    <div>
                        <div className="text-[10px] text-stone-500 mb-0.5">Expense head</div>
                        <ComboSelect
                            value={line.ledger_id || ""}
                            onValueChange={(v) => onChange({ ledger_id: v })}
                            options={headOptions}
                            placeholder="Search: software, rent, fuel…"
                            testId={`ec-line-${idx}-head`}
                            size="sm"
                        />
                    </div>
                )}
                <div>
                    <div className="text-[10px] text-stone-500 mb-0.5">{simple ? "Amount ₹" : "Taxable ₹"}</div>
                    <Input type="number" step="0.01" placeholder="0.00"
                           value={line.taxable ?? ""}
                           onChange={(e) => onChange({ taxable: e.target.value })}
                           className="se-num text-right h-9"
                           data-testid={`ec-line-${idx}-taxable`} />
                </div>
                {simple ? null : (
                    <>
                        <div>
                            <div className="text-[10px] text-stone-500 mb-0.5">GST %</div>
                            <Input type="number" step="0.01" placeholder="0"
                                   value={line.gst_pct ?? 0}
                                   onChange={(e) => onChange({ gst_pct: Number(e.target.value) })}
                                   className="se-num text-right h-9"
                                   data-testid={`ec-line-${idx}-gstpct`} />
                        </div>
                        <div>
                            <div className="text-[10px] text-stone-500 mb-0.5">GST type</div>
                            <select value={line.gst_type || ""}
                                    onChange={(e) => onChange({ gst_type: e.target.value })}
                                    disabled={!(line.gst_pct > 0)}
                                    className="border rounded-sm px-2 text-xs h-9 w-full disabled:bg-stone-100 disabled:text-stone-400"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid={`ec-line-${idx}-gsttype`}>
                                <option value="">—</option>
                                <option value="intra">CGST + SGST (within state)</option>
                                <option value="inter">IGST (interstate)</option>
                            </select>
                        </div>
                    </>
                )}
            </div>
            {(!simple && line.gst_pct > 0 && Number(line.taxable) > 0) ? (
                <div className="text-[10px] text-stone-500 pt-0.5">
                    GST @ {line.gst_pct}% = <b>₹{inr(gstPreview)}</b> · Line total: <b>₹{inr(total)}</b>
                    {!line.gst_type ? <span className="text-rose-600 ml-2">⚠ pick GST type or it will be booked as a sunk cost, not input credit</span> : null}
                </div>
            ) : null}
        </div>
    );
}

function AttachmentField({ attachment, onUpload, onRemove }) {
    if (attachment) {
        return (
            <div className="flex items-center gap-2 border rounded-sm px-3 py-2 h-9"
                 style={{ borderColor: "var(--se-border)", background: "#f0fdf4" }}>
                <Paperclip size={13} className="text-emerald-700" />
                <div className="text-xs truncate flex-1" title={attachment.filename}>{attachment.filename || "attached"}</div>
                <a href={`${API}/documents/${attachment.document_id}/download`} target="_blank" rel="noreferrer"
                   className="text-emerald-700 hover:underline text-xs">view</a>
                <button onClick={onRemove} className="text-stone-400 hover:text-rose-600">
                    <XCircle size={13} />
                </button>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2">
            <label className="flex-1 flex items-center justify-center gap-2 border rounded-sm px-3 py-2 h-9 cursor-pointer hover:bg-stone-50"
                   style={{ borderColor: "var(--se-border)" }}>
                <Paperclip size={13} />
                <span className="text-xs">Upload proof</span>
                <input type="file" className="hidden" accept="image/*,application/pdf"
                       onChange={(e) => onUpload(e.target.files?.[0])} data-testid="ec-upload-input" />
            </label>
            <CameraCaptureButton onFile={onUpload} label="" />
        </div>
    );
}

/* ---------------------------- View / detail dialog ---------------------------- */
function ClaimViewDialog({ cid, onClose, onChanged }) {
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/expense-claims/${cid}`);
            setDoc(data);
        } catch (e) { toast.error(formatApiError(e)); onClose(); }
    }, [cid, onClose]);
    useEffect(() => { load(); }, [load]);

    if (!doc) return null;
    const chip = STATUS_CHIPS[doc.status] || STATUS_CHIPS.draft;

    const doAction = async (fn) => {
        setBusy(true);
        try { await fn(); await load(); onChanged && onChanged(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ec-view-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-3">
                        {doc.claim_no}
                        <span className="text-[11px] rounded px-2 py-0.5 font-medium"
                              style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Claimant" value={`${doc.claimant_name} · ${doc.claimant_role || ""}`} />
                        <Field label="Date paid" value={doc.claim_date} />
                        <Field label="Submitted" value={formatDate(doc.submitted_at)} />
                        {doc.approved_at ? <Field label="Approved" value={`${formatDate(doc.approved_at)}`} /> : null}
                        {doc.disbursement_date ? <Field label="Reimbursed" value={`${doc.disbursement_date} via ${doc.source_ledger_name}`} /> : null}
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Reason</div>
                        <div>{doc.reason}</div>
                    </div>
                    {doc.attachment ? (
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Attachment</div>
                            <a href={`${API}/documents/${doc.attachment.document_id}/download`} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-xs">
                                <Paperclip size={12} /> {doc.attachment.filename || "view proof"}
                            </a>
                        </div>
                    ) : null}
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Line items</div>
                        <table className="se-table text-xs">
                            <thead><tr><th>Description</th><th>Expense head</th><th className="text-right">Taxable</th><th className="text-right">GST</th><th className="text-right">Total</th></tr></thead>
                            <tbody>
                                {doc.lines.map((l) => (
                                    <tr key={l.id}>
                                        <td>{l.description}</td>
                                        <td>{l.ledger_name}</td>
                                        <td className="se-num text-right">₹ {inr(l.taxable)}</td>
                                        <td className="se-num text-right">{l.gst_pct ? `${l.gst_pct}% · ₹${inr(l.gst_amount || 0)}` : "—"}</td>
                                        <td className="se-num text-right font-medium">₹ {inr((l.taxable || 0) + (l.gst_amount || 0))}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="4" className="text-right font-medium">Total</td>
                                    <td className="se-num text-right font-medium" style={{ color: "var(--se-brand)" }}>₹ {inr(doc.total_amount)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    {doc.status === "rejected" && doc.rejection_reason ? (
                        <div className="rounded-md p-3 bg-rose-50 text-rose-800 text-xs">
                            <b>Rejection reason:</b> {doc.rejection_reason}
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    {doc.status === "pending_approval" ? (
                        <>
                            <Button variant="outline" onClick={() => {
                                const reason = window.prompt("Reason for rejection?");
                                if (!reason?.trim()) return;
                                doAction(() => api.post(`/expense-claims/${doc.id}/reject`, { reason: reason.trim() }));
                            }} disabled={busy}
                                    className="text-rose-600 hover:bg-rose-50"
                                    data-testid="ec-view-reject">Reject</Button>
                            <Button onClick={() => doAction(() => api.post(`/expense-claims/${doc.id}/approve`))}
                                    disabled={busy}
                                    style={{ background: "#16a34a", color: "#fff" }}
                                    data-testid="ec-view-approve">
                                <CheckCircle size={13} className="mr-1" /> Approve
                            </Button>
                        </>
                    ) : null}
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
            <div>{value}</div>
        </div>
    );
}

/* ---------------------------- Disburse dialog ---------------------------- */
function DisburseDialog({ claim, ledgers, onClose, onDone }) {
    const cashBankLedgers = useMemo(
        () => (ledgers || []).filter((l) => l.party_type === "bank" || l.party_type === "cash"),
        [ledgers]
    );
    const [sourceId, setSourceId] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [refNo, setRefNo] = useState("");
    const [busy, setBusy] = useState(false);

    const source = useMemo(
        () => cashBankLedgers.find((l) => l.id === sourceId),
        [sourceId, cashBankLedgers]
    );
    const isSystem = source?.system;

    const submit = async () => {
        if (!sourceId) return toast.error("Pick where the money will come from");
        setBusy(true);
        try {
            const { data } = await api.post(`/expense-claims/${claim.id}/disburse`, {
                source_ledger_id: sourceId, date, ref_no: refNo,
            });
            if (data.mode === "immediate") {
                toast.success("Reimbursed instantly · journal posted");
            } else {
                toast.success(`Queued for ${data.bank_ledger_name} — will auto-post when the statement matches`);
            }
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="ec-disburse-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Reimburse {claim.claim_no}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e" }}>
                        Reimbursing <b>{claim.claimant_name}</b> · <b>₹ {inr(claim.total_amount)}</b>
                    </div>
                    <div>
                        <Label>Pay from</Label>
                        <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}
                                className="w-full border rounded-sm px-3 py-2 text-sm"
                                style={{ borderColor: "var(--se-border)" }}
                                data-testid="ec-disburse-source">
                            <option value="">Pick a cash or bank ledger…</option>
                            {cashBankLedgers.map((l) => (
                                <option key={l.id} value={l.id}>
                                    {l.name} {l.system ? "(instant)" : "(via bank statement)"}
                                </option>
                            ))}
                        </select>
                        {source ? (
                            <div className="text-[11px] text-stone-500 mt-1">
                                {isSystem
                                    ? <><Wallet size={10} className="inline mr-1" /> Cash / Fiat — posts to ledger immediately.</>
                                    : <>Real bank — we&apos;ll queue the intent. Journal posts when you upload the statement and match the debit line.</>
                                }
                            </div>
                        ) : null}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Date</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                                   data-testid="ec-disburse-date" />
                        </div>
                        <div>
                            <Label>Reference (optional)</Label>
                            <Input value={refNo} onChange={(e) => setRefNo(e.target.value)}
                                   placeholder="UPI ref, cheque no, …"
                                   data-testid="ec-disburse-ref" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !sourceId}
                            style={{ background: "#0f766e", color: "#fff" }}
                            data-testid="ec-disburse-submit">
                        {busy ? "…" : "Reimburse"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
